import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { CareShell } from "@/components/CareShell";
import { HC_TILES, HC_CARD } from "@/components/care/HealthcareCard";
import { HcHeader, HcEmergency } from "@/components/care/HcHeader";
import { HcLabCatalog } from "@/components/care/HcLabCatalog";
import { HcServicePick } from "@/components/care/HcServicePick";
import { HcSummary } from "@/components/care/HcSummary";
import { Loader2, ChevronRight, ShieldCheck, CalendarCheck } from "lucide-react";

function Landing({ cat }) {
    return (
        <>
            <HcHeader title="Healthcare at Home" back="/care" />
            <div className="bg-aqua-light/60 px-5 py-5"><p className="max-w-xs text-base font-semibold text-navy/80">Choose a healthcare service for your loved ones at home.</p></div>
            <div className="space-y-3 px-5 pt-4 pb-8">
                {HC_TILES.map((t, i) => {
                    const on = cat.kinds[t.kind]?.available;
                    return (
                        <Link key={t.kind} to={`/care/healthcare/${t.kind}`} data-testid={`hc-explore-${t.kind}`} style={{ animationDelay: `${i * 70}ms` }}
                            className={`${HC_CARD} flex animate-in fade-in slide-in-from-bottom-2 items-center gap-4 p-4 transition-transform duration-200 hover:-translate-y-0.5`}>
                            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-aqua-light text-teal"><t.icon size={24} /></span>
                            <div className="min-w-0 flex-1"><p className="font-heading font-bold text-navy">{t.label}</p><p className="text-xs text-navy/55">{t.desc}</p>
                                <span data-testid={`hc-explore-status-${t.kind}`} className={`mt-1 inline-block text-[10px] font-bold ${on ? "text-emerald-600" : "text-navy/40"}`}>{on ? "Available" : "Coming soon"}</span></div>
                            <ChevronRight size={18} className="text-navy/40" />
                        </Link>
                    );
                })}
                <div className="flex gap-3 rounded-2xl bg-aqua-light/70 p-4" data-testid="hc-trust-card">
                    <ShieldCheck size={24} className="shrink-0 text-teal" />
                    <div><p className="text-sm font-bold text-navy">Verified & Qualified Providers</p><p className="text-xs text-navy/60">Every nurse, doctor and lab is checked by HOMIIE, with qualification and registration verified before assignment. Prices are shown upfront.</p></div>
                </div>
                <Link to="/care/healthcare/bookings" data-testid="hc-my-bookings-link" className={`${HC_CARD} flex items-center gap-3 p-4 text-sm font-bold text-navy`}><CalendarCheck size={19} className="text-teal" /> My healthcare bookings <ChevronRight size={16} className="ml-auto text-navy/40" /></Link>
                <HcEmergency />
            </div>
        </>
    );
}

export default function HealthcareBook() {
    const { kind } = useParams();
    const nav = useNavigate();
    const [cat, setCat] = useState(null);
    const [codes, setCodes] = useState([]);
    const [step, setStep] = useState("pick");
    useEffect(() => { api.get("/care/healthcare/catalog").then((r) => setCat(r.data)).catch(() => setCat({ kinds: {}, items: [] })); }, []);
    useEffect(() => { setCodes([]); setStep("pick"); }, [kind]);
    if (!cat) return <CareShell><div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-teal" /></div></CareShell>;
    if (!kind) return <CareShell><Landing cat={cat} /></CareShell>;
    const tile = HC_TILES.find((t) => t.kind === kind);
    const items = cat.items.filter((i) => i.kind === kind);
    if (step === "summary") {
        return <CareShell><HcHeader title="Booking Summary" onBack={() => setStep("pick")} /><HcSummary kind={kind} codes={codes} /></CareShell>;
    }
    return (
        <CareShell>
            <HcHeader title={tile?.label || "Healthcare"} back="/care/healthcare" />
            {!items.length ? (
                <div className="px-5"><div data-testid="hc-unavailable" className={`${HC_CARD} p-6 text-center text-sm text-navy/60`}>{tile?.label} isn't available in your area yet. We open bookings only once verified providers and confirmed prices are in place.
                    <button onClick={() => nav("/care/healthcare")} className="mt-3 block w-full text-sm font-bold text-teal">See other services</button></div></div>
            ) : kind === "lab" ? <HcLabCatalog items={items} codes={codes} setCodes={setCodes} onContinue={() => setStep("summary")} />
                : <HcServicePick kind={kind} items={items} codes={codes} setCodes={setCodes} verified={cat.kinds[kind]?.verified_providers} onContinue={() => setStep("summary")} />}
        </CareShell>
    );
}
